import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import { Color } from '@amcharts/amcharts4/core';
import { faChartBar, faTable } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import WidgetTable, { WidgetTableColumnType } from '../../../../components/widget-table/widget-table';
import { ApplicationState } from '../../../../store';
import {
    DateRangeWithFunctionalLocationsRequest,
    fetchTiresFittedBrandDistributionRequest
} from '../../../../store/analytics';
import { TireBrandDistributionData } from '../../../../store/analytics/types/tire-brand-distribution.types';
import { ViewMode } from '../../../../types/view-mode';
import { generateUUID } from '../../../../utils/uuid-helpers';
import { ControlsSnapshot } from '../../dashboard-controls/controls-snapshot';
import { SingleChartWidgetBase } from '../single-chart-widget-base';
import {
    addStandardTooltipStyle,
    addStrokeToColumnChart,
    createXYChart,
} from '../utils/chart-utils';
import {
    createDateRangeWithFunctionalLocationsRequest,
    hasInspectionPeriod
} from '../utils/widget-helper';
import WidgetLoading from '../widget-loading';
import {brandField, buildChartData, newField, reusableField} from './chart-data-adapter';
import styles from './tires-fitted-brand-distribution-widget.module.scss';
import {colorBlue4, colorBlue8} from "../utils/chart-colors";
class TiresFittedBrandDistributionWidget extends SingleChartWidgetBase<AllProps, AllState, TireBrandDistributionData[]> {
    private chartId: string;

    constructor(props) {
        super(props);
        this.chartId = generateUUID();

        this.state = {
            viewMode: ViewMode.chart,
        };
    }

    public render(): JSX.Element {
        const { t, data } = this.props;
        const { viewMode } = this.state;

        return (
            <div className={styles.widgetContainer}>
                {data ? (
                    <div className={styles.chartView}>
                        <div className={styles.chartHeader}>
                            <div className={styles.headerTitle}>{t('Tires Fitted Brand Distribution')}</div>
                            <div className={styles.interactions}>
                                <div className={styles.headerToggle} onClick={() => this.toggleViewMode()}>
                                    {viewMode === ViewMode.chart ? (
                                        <FontAwesomeIcon icon={faTable} />
                                    ) : (
                                        <FontAwesomeIcon icon={faChartBar} />
                                    )}
                                </div>
                            </div>
                        </div>
                        {viewMode === ViewMode.chart ? (
                            <div className={styles.chartContent}>
                                <div id={this.chartId} style={{ width: '100%', minHeight: `${(data.length * 30) + 80}px` }} />
                            </div>
                        ) : (
                            <div className={styles.tableContent}>
                                <WidgetTable
                                    config={{
                                        columns: [
                                            {
                                                title: 'Brand',
                                                property: brandField,
                                                type: WidgetTableColumnType.upperText,
                                                width: '480px',
                                            },
                                            {
                                                title: '# New Tires',
                                                property: newField,
                                                type: WidgetTableColumnType.number,
                                            },
                                            {
                                                title: '# Reusable Tires',
                                                property: reusableField,
                                                type: WidgetTableColumnType.number,
                                            },
                                        ],
                                    }}
                                    data={data}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <WidgetLoading />
                )}
            </div>
        );
    }

    protected fetchData(snapshot: ControlsSnapshot): void {
        const { dispatchFetchTiresFittedBrandDistributionRequest } = this.props;
        dispatchFetchTiresFittedBrandDistributionRequest(createDateRangeWithFunctionalLocationsRequest(this.props.controlsSnapshot));
    }

    private toggleViewMode(): void {
        const { viewMode } = this.state;
        const updatedViewMode = viewMode === ViewMode.chart ? ViewMode.table : ViewMode.chart;
        this.setState({ viewMode: updatedViewMode }, () => {
            if (this.state.viewMode === ViewMode.chart) {
                this.refreshChart();
            } else {
                this.disposeChart();
            }
        });
    }

    protected validateControlsSnapshot(snapshot: ControlsSnapshot): boolean {
        return hasInspectionPeriod(snapshot);
    }

    protected createChart(data: TireBrandDistributionData[]): am4charts.XYChart {
        const chart = createXYChart(this.chartId);

        const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = brandField;
        categoryAxis.title.text = this.props.t('Brand');
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.minGridDistance = 20;

        categoryAxis.renderer.labels.template.wrap = true;
        categoryAxis.renderer.labels.template.maxWidth = 320;
        categoryAxis.renderer.labels.template.tooltipText = "{category}";

        const valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
        valueAxis.min = 0;
        valueAxis.title.text = this.props.t('# Units');
        chart.data = buildChartData(data, this.props.t);
        this.addAllSeries(chart);
        
        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';
        chart.legend.reverseOrder = true;

        return chart;
    }

    private addAllSeries(chart: any): void {
        this.addSeries(chart,this.props.t('Reusable Tires'),am4core.color(colorBlue4),reusableField);
        this.addSeries(chart,this.props.t('New Tires'),am4core.color(colorBlue8),newField);
    }
    
    private addSeries(chart:any,seriesName:string,color:Color, valueXField:string):void{
        const series = chart.series.push(new am4charts.ColumnSeries());
        series.name = seriesName;
        series.dataFields.categoryY = brandField;
        series.dataFields.valueX = valueXField;
        series.columns.template.tooltipText = `{categoryY}: [bold]{valueX} ${
            seriesName
        }[/]`;
        series.columns.template.fillOpacity = 0.8;
        series.columns.template.maxHeight = 100;
        series.columns.template.adapter.add('fill', (fill, target) => color);

        const seriesValueLabel = series.bullets.push(new am4charts.LabelBullet());
        seriesValueLabel.label.text = '{valueX}';
        seriesValueLabel.label.horizontalCenter = 'left';
        seriesValueLabel.label.dx = 10;
        seriesValueLabel.label.hideOversized = false;
        seriesValueLabel.label.truncate = false;

        addStrokeToColumnChart(series);
        addStandardTooltipStyle(series);
    }
}

const mapStateToProps = ({ analytics }: ApplicationState) => ({
    data: analytics.tiresFittedBrandDistributionData,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    dispatchFetchTiresFittedBrandDistributionRequest: (request: DateRangeWithFunctionalLocationsRequest) =>
        dispatch(fetchTiresFittedBrandDistributionRequest(request)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(TiresFittedBrandDistributionWidget));

interface PropsFromState {
    data: TireBrandDistributionData[];
    fleetCustomerId: string;
}

interface PropsFromDispatch {
    dispatchFetchTiresFittedBrandDistributionRequest: typeof fetchTiresFittedBrandDistributionRequest;
}

interface OwnProps {
    controlsSnapshot: ControlsSnapshot;
}

type AllProps = OwnProps & PropsFromState & PropsFromDispatch & WithTranslation;

interface OwnState {
    viewMode: ViewMode;
}

type AllState = OwnState;
